// ── Types ────────────────────────────────────────────────────────────────────
import type * as types from "./types";
export type * from "./types";

// ── Client ───────────────────────────────────────────────────────────────────

const LOCALSTORAGE_TOKEN_KEY = "ema-token";

export default class QuikTripClient {
  private readonly baseUrl: string;
  private token: string | null = null;
  private readonly cache = new Map<string, Promise<unknown>>();

  private cached<T>(key: string, fn: () => Promise<T>): Promise<T> {
    if (!this.cache.has(key)) this.cache.set(key, fn());
    return this.cache.get(key) as Promise<T>;
  }

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
    if (this.token) console.debug("EMA Token found.");
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const ct = response.headers.get("content-type") ?? "";
    if (!ct.includes("application/json"))
      throw new Error("Unexpected response: not JSON (are you logged in?)");
    return response.json();
  }

  private async get<T>(path: string): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const response = await fetch(`${this.baseUrl}/${path}`, { headers });
    return this.parseResponse<T>(response);
  }

  private async post<T>(path: string, data?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const response = await fetch(`${this.baseUrl}/${path}`, {
      method: "POST",
      headers,
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    return this.parseResponse<T>(response);
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  async login(userName: string, password: string): Promise<string> {
    const response = await this.post<types.LoginResponse>(
      "authentication/token",
      {
        userName,
        password,
      },
    );
    this.token = response.token;
    localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, this.token);
    return this.token;
  }

  isLoggedIn(): boolean {
    return this.token !== null;
  }

  logout(): void {
    this.token = null;
    this.cache.clear();
    localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
  }

  // ── Employee ──────────────────────────────────────────────────────────────

  getEmployeeInfo(): Promise<types.EmployeeInfo> {
    return this.cached("employeeInfo", () =>
      this.get("employee/GetEmployeeInfo"),
    );
  }

  // ── Pay Stubs ─────────────────────────────────────────────────────────────

  getAllPayStubs(): Promise<types.PayStubPeriod[]> {
    return this.cached("allPayStubs", () => this.get("payStub/GetAllPayStubs"));
  }

  // ── Work Schedule ─────────────────────────────────────────────────────────

  getWorkSchedule(): Promise<types.WorkSchedule> {
    return this.cached("workSchedule", () =>
      this.get("workSchedule/GetWorkSchedule"),
    );
  }

  // ── Time Off ──────────────────────────────────────────────────────────────

  getAllTimeOff(): Promise<types.TimeOffData> {
    return this.cached("allTimeOff", () => this.get("timeOff/GetAllTimeOff"));
  }

  getRequestPageData(id: string): Promise<unknown> {
    return this.get(`timeOff/GetRequestPageData/${id}`);
  }

  createTimeOffRequest(request: types.CreateTimeOffRequest): Promise<unknown> {
    return this.post("timeOff/CreateTimeOffRequest", request);
  }

  // ── Extra Shifts ──────────────────────────────────────────────────────────

  getExtraShiftsByWeek(): Promise<types.ExtraShiftWeek[]> {
    return this.get("extraShift/GetExtraShiftsByWeek");
  }

  getClerkExtraShiftSchedule(): Promise<types.ExtraShift[]> {
    return this.get("extraShift/GetClerkExtraShiftSchedule");
  }

  createExtraShift(request: types.CreateExtraShiftRequest): Promise<unknown> {
    return this.post("extraShift/ClerkExtraShiftCreate", request);
  }

  acceptExtraShift(extraShiftId: string): Promise<unknown> {
    return this.post(`extraShift/ExtraShiftAcceptAndCheckEr/${extraShiftId}`);
  }

  acceptTimeOffExtraShift(clientDateTime: string): Promise<unknown> {
    return this.post(
      `extraShift/ExtraShiftAcceptTimeOffRequest?clientDateTime=${encodeURIComponent(clientDateTime)}`,
    );
  }

  cancelExtraShift(extraShiftId: string): Promise<unknown> {
    return this.post(`extraShift/ExtraShiftCancel/${extraShiftId}`);
  }

  // ── Notifications ─────────────────────────────────────────────────────────

  getNotificationTypes(): Promise<types.NotificationType[]> {
    return this.get("notification/GetNotificationTypes");
  }

  getSentUserNotifications(): Promise<unknown[]> {
    return this.get("notification/GetSentUserNotifications");
  }

  // ── Stores ────────────────────────────────────────────────────────────────

  getDivisionStores(divisionId: string): Promise<types.Store[]> {
    return this.get(`store/GetDivisionStores/${divisionId}`);
  }
}
